/**
 * Бой атаки (режим «Карта»): план защитников-зомби. Остальная логика боя — в `battle.ts` (`BattleInit.attack`).
 * Зомби выходят волнами из базы: `waveSize` штук каждые `waveGapTicks`, виды — как у зомби волны `zombieWave`.
 */
import type { AttackDefenseZombies, BalanceData, MapDef } from "@newfungame/contracts";
import { divTrunc } from "./math";
import type { Rng } from "./rng";
import { pickKind, type SpawnEntry, type WavePlan } from "./waves";
import type { AttackLayout } from "./layout";

/** 16 направлений выхода из базы, тысячные доли. */
const DIRS: readonly (readonly [number, number])[] = [
  [1000, 0],
  [924, 383],
  [707, 707],
  [383, 924],
  [0, 1000],
  [-383, 924],
  [-707, 707],
  [-924, 383],
  [-1000, 0],
  [-924, -383],
  [-707, -707],
  [-383, -924],
  [0, -1000],
  [383, -924],
  [707, -707],
  [924, -383],
];

export function mapDef(balance: BalanceData): MapDef {
  if (!balance.beta) throw new RangeError("balance has no beta section");
  return balance.beta.map;
}

/** План боя атаки: появления защитников по возрастанию тика. Для защиты героями — без появлений. */
export function attackPlan(
  balance: BalanceData,
  defense: AttackDefenseZombies | null,
  rng: Rng,
  layout: AttackLayout,
  wave: number,
): WavePlan {
  const m = mapDef(balance).attack;
  const spawns: SpawnEntry[] = [];
  if (defense) {
    let pool = balance.zombies.filter((z) => z.minWave <= defense.zombieWave);
    if (pool.length === 0) {
      const min = Math.min(...balance.zombies.map((z) => z.minWave));
      pool = balance.zombies.filter((z) => z.minWave === min);
    }
    const half = divTrunc(layout.field.width, 2);
    const point = (): { x: number; y: number } => {
      const d = DIRS[rng.int(0, DIRS.length - 1)]!;
      return {
        x: Math.max(-half, Math.min(half, layout.hq.x + divTrunc(d[0] * m.spawnRadius, 1000))),
        y: Math.max(0, Math.min(layout.field.depth, layout.hq.y + divTrunc(d[1] * m.spawnRadius, 1000))),
      };
    };
    const end = m.introTicks + m.timeLimitTicks;
    for (let tick = m.introTicks + defense.firstWaveTicks; tick < end; tick += defense.waveGapTicks) {
      for (let i = 0; i < defense.waveSize; i++) {
        const p = point();
        spawns.push({ tick: tick + i * 4, kind: pickKind(pool, rng), bossId: null, x: p.x, y: p.y });
      }
    }
    if (defense.bossId) {
      const p = point();
      spawns.push({
        tick: m.introTicks + divTrunc(m.timeLimitTicks * 2, 5),
        kind: "boss",
        bossId: defense.bossId,
        x: p.x,
        y: p.y,
      });
    }
    spawns.sort((a, b) => a.tick - b.tick);
  }
  return {
    wave,
    isBoss: !!defense?.bossId,
    bossId: defense?.bossId ?? null,
    introTicks: m.introTicks,
    hpPermille: defense?.hpPermille ?? 1000,
    spawns,
  };
}
